interface Reminder {
  id: string;
  title: string;
  description?: string;
  datetime: Date;
  priority: 'low' | 'medium' | 'high';
  completed: boolean;
  tags?: string[];
}

class ReminderService {
  private reminders: Reminder[] = [];

  addReminder(reminder: Omit<Reminder, 'id'>): Reminder {
    const newReminder = {
      ...reminder,
      id: Date.now().toString()
    };
    
    this.reminders.push(newReminder);
    this.scheduleNotification(newReminder);
    return newReminder;
  }

  private scheduleNotification(reminder: Reminder) {
    const now = new Date();
    const timeUntilReminder = reminder.datetime.getTime() - now.getTime();

    if (timeUntilReminder > 0) {
      setTimeout(() => {
        this.notify(reminder);
      }, timeUntilReminder);
    }
  }

  private notify(reminder: Reminder) {
    if (!reminder.completed) {
      // Verifica suporte a notificações
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Lembrete', {
          body: reminder.title,
          icon: '/icon.png'
        });
      }
    }
  }

  getReminders(filter?: {
    completed?: boolean;
    priority?: Reminder['priority'];
    tags?: string[];
  }): Reminder[] {
    let filtered = this.reminders;

    if (filter) {
      if (filter.completed !== undefined) {
        filtered = filtered.filter(r => r.completed === filter.completed);
      }
      if (filter.priority) {
        filtered = filtered.filter(r => r.priority === filter.priority);
      }
      if (filter.tags) {
        filtered = filtered.filter(r => 
          r.tags?.some(tag => filter.tags?.includes(tag))
        );
      }
    }

    return filtered.sort((a, b) => a.datetime.getTime() - b.datetime.getTime());
  }

  updateReminder(id: string, updates: Partial<Omit<Reminder, 'id'>>): Reminder | null {
    const index = this.reminders.findIndex(r => r.id === id);
    if (index === -1) return null;

    const updatedReminder = {
      ...this.reminders[index],
      ...updates
    };

    this.reminders[index] = updatedReminder;
    return updatedReminder;
  }

  deleteReminder(id: string): boolean {
    const index = this.reminders.findIndex(r => r.id === id);
    if (index === -1) return false;

    this.reminders.splice(index, 1);
    return true;
  }

  parseVoiceCommand(command: string): Partial<Reminder> {
    const priorityMatch = command.match(/prioridade (alta|média|baixa)/i);
    const dateMatch = command.match(/(?:em|dia|para) (\d{1,2}\/\d{1,2}(?:\/\d{4})?)/);
    const timeMatch = command.match(/(?:às|as) (\d{1,2}:\d{2})/);
    
    const reminder: Partial<Reminder> = {
      title: command,
      priority: priorityMatch 
        ? (priorityMatch[1].toLowerCase() === 'alta' ? 'high' 
          : priorityMatch[1].toLowerCase() === 'média' ? 'medium' 
          : 'low')
        : 'medium'
    };

    if (dateMatch || timeMatch) {
      const now = new Date();
      const date = dateMatch 
        ? dateMatch[1].split('/').map(Number)
        : [now.getDate(), now.getMonth() + 1, now.getFullYear()];
      
      const time = timeMatch
        ? timeMatch[1].split(':').map(Number)
        : [now.getHours(), now.getMinutes()];

      reminder.datetime = new Date(
        date[2] || now.getFullYear(),
        (date[1] || (now.getMonth() + 1)) - 1,
        date[0] || now.getDate(),
        time[0],
        time[1]
      );
    }

    return reminder;
  }
}

export const reminderService = new ReminderService();
